// frontend/src/pages/PaymentPage.js
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "../utils/axiosConfig"; // 包含 CSRF 設定
import API_URL from "../config";

const PaymentPage = () => {
  const { t } = useTranslation();

  // 從 URL 查詢參數取得方案、方案類型與初始計費週期
  const queryParams = new URLSearchParams(window.location.search);
  const plan = queryParams.get("plan") || "standard";
  const planType = queryParams.get("planType") || "individual";
  const initialBillingCycle = queryParams.get("billingPeriod") || "monthly";

  // 定義定價物件（每月基本價格）
  const pricing = {
    "free": 0,               // 免費版
    "basic-label": 300,      // 基礎版 - 標籤
    "basic-risk": 600,       // 基礎版 - 風險評估
    "advanced": 3000         // 進階版 - 標籤+風險評估
  };

  // 固定計算各方案價格
  const monthlyPrice = pricing[plan];
  const annuallyPrice = pricing[plan] * 12 * 0.8;

  // 選擇的計費週期與付款方式（其餘狀態保持不變）
  const [selectedBillingCycle, setSelectedBillingCycle] = useState(initialBillingCycle);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("creditCard");
  const [promotionCode, setPromotionCode] = useState("");
  const [isApplying, setIsApplying] = useState(false);

  // 驗證使用者登入狀態
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // 取得當前使用者資訊，參考 WalletRisk.js
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/current_user`, { withCredentials: true });
        if (response.data) {
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  // 根據所選計費週期計算總計金額
  const getFinalPrice = () => {
    if (selectedBillingCycle === "monthly") return Math.round(monthlyPrice);
    if (selectedBillingCycle === "annually") return Math.round(annuallyPrice);
    return Math.round(monthlyPrice);
  };

  // 取得計費週期的文字說明（用於摘要區）
  const getPeriodLabel = () => {
    if (selectedBillingCycle === "annually") return t("ApiPrice.periodAnnual", "for 12 months");
    return t("ApiPrice.month", "per month");
  };

  // 訂單摘要中對應方案的顯示文字，與 ApiPrice.js 保持一致
  const planDescriptions = {
    "free": t("ApiPrice.free", "Free"),
    "basic-label": t("ApiPrice.basicLabel", "Basic - Label"),
    "basic-risk": t("ApiPrice.basicRisk", "Basic - Risk Assessment"),
    "advanced": t("ApiPrice.advanced", "Advanced - Label & Risk")
  };

  // 促銷碼輸入更新
  const handlePromotionCodeChange = (e) => {
    setPromotionCode(e.target.value);
  };

  // 送出促銷碼
  const applyPromotionCode = async () => {
    if (!promotionCode.trim()) {
      toast.error(t("payment.error.emptyPromoCode", "請輸入促銷碼"));
      return;
    }
    setIsApplying(true);
    try {
      // 此處直接成功提示，實際請根據 API 呼叫調整
      toast.success(t("payment.success.promoApplied", "促銷碼已成功套用"));
    } catch (error) {
      toast.error(t("payment.error.invalidPromoCode", "促銷碼無效"));
    } finally {
      setIsApplying(false);
    }
  };

  // 處理永豐付款：呼叫後端訂閱訂單建立 API，取得付款連結後導向
  const handleSinoPacPayment = async () => {
    try {
      toast.info(t("payment.processing", "付款處理中..."));
      const OrderNo = "SUB" + Date.now();
      const finalPrice = getFinalPrice();
      const payload = {
        OrderNo,                           // 訂單編號
        Amount: Number(finalPrice),       // 付款金額
        CurrencyID: "TWD",                 // 幣別：新台幣
        PayType: "C",                      // 固定使用信用卡支付
        QRCodeStatus: "Y",                 // 啟用 QR Code
        QRCodeSize: 350,                   // QR Code 大小
        CardParam: {                       // 信用卡參數
          AutoBilling: "Y"                 // 自動請款
        },
        PrdtName: "信用卡訂單",            // 商品名稱
        ReturnURL: `https://damenkyt.com/payment-success`,   // 付款成功返回網址
        BackendURL: `https://damenkyt.com/payment-notification`, // 付款通知網址
        plan,                              // 方案代號
        planType: selectedBillingCycle === "monthly" ? "monthly" : "yearly", // 根據用戶選擇設定
        billingPeriod: selectedBillingCycle === "monthly" ? "1" : "12", // 轉換為後端要求的格式
        promotionCode: promotionCode.trim() || null // 促銷碼
      };

      console.log("準備發送的 payload:", payload);

      const response = await axios.post(
        `${API_URL}/api/payment/order/subscribe`,
        payload,
        { withCredentials: true }
      );
      if (response.data && response.data.PaymentURL) {
        window.location.href = response.data.PaymentURL;
      } else {
        toast.error(t("payment.error.noPaymentLink", "無法取得付款連結，請稍後再試。"));
      }
    } catch (error) {
      toast.error(
        t("payment.error.paymentFailed", "付款失敗：") +
          (error.response?.data?.error || error.message)
      );
    }
  };

  // 處理綠界付款
  const handleECPayPayment = async () => {
    try {
      toast.info(t("payment.processing", "付款處理中..."));
      const finalPrice = getFinalPrice();
      
      // 構建基於域名的絕對 URL，避免路由問題
      // 在生產環境使用域名，開發環境使用 localhost
      const baseUrl = process.env.NODE_ENV === 'production' 
        ? 'https://damenkyt.com'
        : 'http://localhost:3000';
      
      const payload = {
        amount: Math.round(finalPrice),  // 綠界要求金額為整數
        productName: `${planDescriptions[plan]} ${selectedBillingCycle === "monthly" ? t("payment.monthly", "每月") : t("payment.annually", "每年")}`,
        userId: localStorage.getItem('userId') || '',  // 如果有儲存用戶ID
        planId: plan,
        billingPeriod: selectedBillingCycle === "monthly" ? "1" : "12",
        returnUrl: `${baseUrl}/payment-success`, // 前端頁面(GET請求)，確保引導到首頁
        notifyUrl: `${baseUrl}/api/ecpay/payment-notify` // 後端API(接受POST)
      };

      console.log("綠界定期定額付款請求:", payload);

      const response = await axios.post(
        `${API_URL}/api/ecpay/create-subscription-order`,
        payload,
        { withCredentials: true }
      );

      if (response.data && response.data.success) {
        // 保存訂單編號到localStorage，以便支付成功頁面可以獲取
        localStorage.setItem("currentOrderNo", response.data.orderNo);
        localStorage.setItem("paymentGateway", "ecpay");
        localStorage.setItem("paymentType", "subscription");
        
        // 創建一個隱藏的表單並提交到綠界
        const formContainer = document.createElement('div');
        formContainer.innerHTML = response.data.htmlRedirectForm;
        document.body.appendChild(formContainer);
        document.forms[0].submit();
      } else {
        toast.error(t("payment.error.noPaymentLink", "無法取得付款連結，請稍後再試。"));
      }
    } catch (error) {
      toast.error(
        t("payment.error.paymentFailed", "付款失敗：") +
          (error.response?.data?.error || error.message)
      );
    }
  };

  // 若未驗證且已完成 loading，顯示登入視窗（僅顯示 Google 登入）
  const googleLoginUrl =
    process.env.NODE_ENV === "production"
      ? `${API_URL}/api/auth/google`
      : "http://localhost:5002/api/auth/google";

  if (!isAuthenticated && !loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg">
          <h2 className="text-xl font-bold mb-4">{t("loginRequired", "請先登入")}</h2>
          <p className="mb-4">{t("pleaseLoginUsingGoogle", "請使用 Google 登入")}</p>
          <button
            onClick={() => (window.location.href = googleLoginUrl)}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 rounded"
          >
            {t("loginWithGoogle", "使用 Google 登入")}
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          {/* Skeleton loading */}
          <div className="h-10 w-64 bg-gray-200 dark:bg-gray-700 rounded mb-6 animate-pulse"></div>
          <div className="h-6 w-32 bg-gray-200 dark:bg-gray-700 rounded mb-4 animate-pulse"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="h-40 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
            <div className="h-40 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
            <div className="h-40 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          </div>
          <div className="h-6 w-32 bg-gray-200 dark:bg-gray-700 rounded mb-4 animate-pulse"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
            <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  // 根據所選計費週期，計算下方摘要顯示的總計價格
  const finalPrice = getFinalPrice();

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* 頁面標題 */}
        <h1 className="text-3xl font-bold text-center mb-8 text-gray-800 dark:text-white">
          {t("payment.title", "選擇付款方式")}
        </h1>

        {/* 計費週期選擇（方案選項顯示固定價格） */}
        <div className="mb-8">
          <h2 className="text-lg font-medium mb-4 text-gray-700 dark:text-gray-300">
            {t("payment.billingCycle", "計費週期")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* 每月方案選項 */}
            <div
              className={`p-6 rounded-lg border-2 cursor-pointer transition-all ${
                selectedBillingCycle === "monthly"
                  ? "border-blue-500"
                  : "border-gray-200 dark:border-gray-700 hover:border-blue-300"
              }`}
              onClick={() => setSelectedBillingCycle("monthly")}
              tabIndex={0}
              role="radio"
              aria-checked={selectedBillingCycle === "monthly"}
              onKeyDown={(e) => e.key === "Enter" && setSelectedBillingCycle("monthly")}
            >
              <div className="text-center">
                <p className="text-gray-600 dark:text-gray-400 font-medium mb-2">
                  {t("payment.monthly", "每月")}
                </p>
                <div className="flex justify-center items-baseline">
                  <span className="text-3xl font-bold text-gray-900 dark:text-white">
                    NT${Math.round(monthlyPrice)}
                  </span>
                  <span className="text-gray-500 dark:text-gray-400 ml-1">
                    / {t("payment.month", "月")}
                  </span>
                </div>
              </div>
            </div>
            {/* 每年方案選項 */}
            <div
              className={`p-6 rounded-lg border-2 cursor-pointer transition-all ${
                selectedBillingCycle === "annually"
                  ? "border-blue-500"
                  : "border-gray-200 dark:border-gray-700 hover:border-blue-300"
              }`}
              onClick={() => setSelectedBillingCycle("annually")}
              tabIndex={0}
              role="radio"
              aria-checked={selectedBillingCycle === "annually"}
              onKeyDown={(e) => e.key === "Enter" && setSelectedBillingCycle("annually")}
            >
              <div className="text-center">
                <p className="text-gray-600 dark:text-gray-400 font-medium mb-2">
                  {t("payment.annually", "每年")}
                </p>
                <div className="flex justify-center items-baseline">
                  <span className="text-3xl font-bold text-gray-900 dark:text-white">
                    NT${Math.round(annuallyPrice)}
                  </span>
                  <span className="text-gray-500 dark:text-gray-400 ml-1">
                    / {t("payment.year", "年")}
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  {t("payment.discountInfoAnnual", "8折優惠")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 付款方式選擇 */}
        <div className="mb-8">
          <h2 className="text-lg font-medium mb-4 text-gray-700 dark:text-gray-300">
            {t("payment.paymentMethod", "付款方式")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* 信用卡 */}
            <div
              className={`p-6 rounded-lg border-2 cursor-pointer transition-all ${
                selectedPaymentMethod === "creditCard"
                  ? "border-blue-500"
                  : "border-gray-200 dark:border-gray-700 hover:border-blue-300"
              }`}
              onClick={() => setSelectedPaymentMethod("creditCard")}
              tabIndex={0}
              role="radio"
              aria-checked={selectedPaymentMethod === "creditCard"}
              onKeyDown={(e) => e.key === "Enter" && setSelectedPaymentMethod("creditCard")}
            >
              <div className="text-center">
                <p className="text-gray-700 dark:text-gray-300 font-medium mb-3">
                  {t("payment.creditCard", "信用卡")}
                </p>
                <div className="flex justify-center space-x-2">
                  <div className="w-10 h-6 bg-blue-500 rounded flex items-center justify-center text-white text-xs">
                    VISA
                  </div>
                  <div className="w-10 h-6 bg-red-500 rounded flex items-center justify-center text-white text-xs">
                    MC
                  </div>
                  <div className="w-10 h-6 bg-blue-400 rounded flex items-center justify-center text-white text-xs">
                    AMEX
                  </div>
                  <div className="w-10 h-6 bg-teal-500 rounded flex items-center justify-center text-white text-xs">
                    UP
                  </div>
                </div>
              </div>
            </div>
            {/* 加密貨幣 */}
            <div
              className={`p-6 rounded-lg border-2 cursor-pointer transition-all ${
                selectedPaymentMethod === "crypto"
                  ? "border-blue-500"
                  : "border-gray-200 dark:border-gray-700 hover:border-blue-300"
              }`}
              onClick={() => setSelectedPaymentMethod("crypto")}
              tabIndex={0}
              role="radio"
              aria-checked={selectedPaymentMethod === "crypto"}
              onKeyDown={(e) => e.key === "Enter" && setSelectedPaymentMethod("crypto")}
            >
              <div className="text-center">
                <p className="text-gray-700 dark:text-gray-300 font-medium mb-3">
                  {t("payment.crypto", "加密貨幣")}
                </p>
                <div className="flex justify-center space-x-2">
                  <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold">
                    T
                  </div>
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                    C
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 促銷碼與訂單摘要 */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* 訂單摘要 */}
            <div>
              <h3 className="text-lg font-medium mb-4 text-gray-700 dark:text-gray-300">
                {t("payment.summary", "訂單摘要")}
              </h3>
              <div className="mb-4">
                <p className="font-medium text-gray-800 dark:text-white">
                  {planDescriptions[plan]}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t("payment.billingCycle", "計費週期")}: {selectedBillingCycle} ({getPeriodLabel()})
                </p>
              </div>
            </div>
            {/* 付款區塊 */}
            <div className="flex flex-col">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300">
                  {t("payment.total", "總計")}
                </h3>
                <span className="text-3xl font-bold text-gray-900 dark:text-white">
                  NT${finalPrice}
                </span>
              </div>
              
              {/* 付款按鈕區域 - 拆分為兩個不同的金流服務 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {/* 永豐銀行支付按鈕 */}
                <button
                  onClick={handleSinoPacPayment}
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <div className="flex items-center justify-center">
                    <span className="mr-2">永豐</span>
                    {selectedPaymentMethod === "crypto"
                      ? t("payment.payWithCrypto", "使用加密貨幣付款")
                      : t("payment.payWithCreditCard", "使用信用卡付款")}
                  </div>
                </button>
                
                {/* 綠界支付按鈕 */}
                <button
                  onClick={handleECPayPayment}
                  className="w-full bg-green-600 text-white py-3 px-4 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <div className="flex items-center justify-center">
                    <span className="mr-2">綠界</span>
                    {selectedPaymentMethod === "crypto"
                      ? t("payment.payWithCrypto", "使用加密貨幣付款")
                      : t("payment.payWithCreditCard", "使用信用卡付款")}
                  </div>
                </button>
              </div>
              
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t("payment.termsAgreement", "點擊付款即同意相關服務條款")}
              </p>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={5000} />
    </div>
  );
};

export default PaymentPage;
